<template>
    <div class="EntityActionsTable">
        <InfiniteScroll @scroll="$emit('scroll-end')">
            <Table>
                <template #toolbar>
                    <slot name="toolbar" />
                </template>
                <template #toolbarEnd>
                    <slot name="toolbarEnd" />
                </template>
                <template #head>
                    <HeadCell class="EntityActionsTable__valueHeader">
                        Value
                    </HeadCell>
                    <HeadCell class="EntityActionsTable__typeHeader">
                        Type
                    </HeadCell>
                    <HeadCell class="EntityActionsTable__entityHeader">
                        Entity
                    </HeadCell>
                    <template v-if="isCompletedTab">
                        <HeadCell class="EntityActionsTable__outcomeHeader">
                            Outcome
                        </HeadCell>
                        <HeadCell class="EntityActionsTable__resolvedByHeader">
                            Resolved By
                        </HeadCell>
                        <HeadCell class="EntityActionsTable__resolvedDateHeader">
                            Resolved Date
                        </HeadCell>
                        <HeadCell class="EntityActionsTable__revertedHeader">
                            Reverted
                        </HeadCell>
                    </template>
                    <template v-else>
                        <HeadCell class="EntityActionsTable__actionRequiredHeader">
                            Action Required
                        </HeadCell>
                        <HeadCell class="EntityActionsTable__dateCreatedHeader">
                            Date Created
                            <EidIcon
                                v-if="dateCreatedSortIcon"
                                class="EntityActionsTable__sortIndicator"
                                :icon-src="dateCreatedSortIcon"
                            />
                        </HeadCell>
                    </template>
                </template>
                <EntityActionsTableRow
                    v-for="entityAction of entityActions"
                    :key="entityAction.id"
                    :entity-action="entityAction"
                    :tab="tab"
                    :requirement-definitions="requirementDefinitions"
                    :search-term="searchTerm"
                    @review="$emit('review', $event)"
                />
                <template v-if="entityActions.length === 0 && finished" #status>
                    <span>{{ emptyReviewsText }}</span>
                </template>
                <template v-else-if="entityActions.length > 0 && finished" #status>
                    <span>All actions displayed ({{ displayedTotal }} total)</span>
                </template>
                <template v-else-if="loading" #status>
                    <LineLoader />
                    <span>Loading...</span>
                </template>
            </Table>
        </InfiniteScroll>
    </div>
</template>

<style lang="scss">
    .EntityActionsTable {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        /* The Value cap below is measured against this box's width. Safe because the parent
           stretches it, so its width never depends on its own content. */
        /* stylelint-disable-next-line property-no-unknown -- stylelint 13 predates container queries */
        container-type: inline-size;

        .Cell,
        .TableHeader {
            /* Row height is driven by how far the Value column wraps, so every other cell is
               pinned to the top instead of floating in the middle of the height Value made. */
            vertical-align: top;
            padding-top: 16px;
            padding-bottom: 16px;
        }

        .TableHeader {
            /* Separates the labels from the data, and leaves room for the sort and search
               controls that sit against the header. */
            padding-bottom: 24px;
        }

        .Row + .Row .Cell {
            /* A hairline rather than zebra striping, which would compete with the Type badge. */
            border-top: 1px solid var(--eidTechGray-25);
        }

        &__sortIndicator {
            margin-left: 6px;
            font-size: var(--text-caption-size, 11px);
        }

        .EntityActionsTableRow__valueText {
            /* Capped so a long entity-provided value wraps instead of pushing every other column
               off the page (PRODUCT-26552). Browsers ignore max-width on a table cell, so the cap
               sits on the text inside it, sized in container units (see container-type above) so
               it stays a share of the table as the viewport changes. The cell's own padding is
               taken off so the column, not just the text, lands near the share. A short value
               keeps its natural width. */
            display: block;
            /* stylelint-disable-next-line unit-no-unknown -- stylelint 13 predates container units */
            max-width: calc(45cqi - 30px);
            overflow-wrap: anywhere;
        }

        &__typeHeader,
        .EntityActionsTableRow__type {
            /* Narrow and fixed, so scope stays scannable however tall rows become. */
            width: 110px;
            white-space: nowrap;
        }

        .Table {
            flex: 1;
            min-height: 0;
        }

        .Table__status {
            min-height: 100px;

            > .LineLoader {
                font-size: 0.8em;

                > .LineLoader__dot {
                    background: currentColor;
                }

                &:not(:only-child) {
                    margin-bottom: 15px;
                }
            }
        }
    }
</style>

<script lang="ts">
    import { PropType } from 'vue';

    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
    import { faArrowDown, faArrowUp } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import InfiniteScroll from '@evidentid/dashboard-commons/components/InfiniteScroll/InfiniteScroll.vue';
    import { LineLoader } from '@evidentid/dashboard-commons/components/LineLoader';
    import { HeadCell, Table } from '@evidentid/dashboard-commons/components/Table';
    import { TprmRequirementDefinition } from '@evidentid/tprm-portal-lib/models/common';
    import { ActionReview, ActionsTab } from '@evidentid/tprm-portal-lib/models/entity-actions-review';
    import { Sorting, SortingDirection } from '@evidentid/universal-framework/sorting';

    import EntityActionsTableRow
        from '@/modules/entity-actions-review/components/EntityActionsTableRow/EntityActionsTableRow.vue';
    import { ACTIONS_SORT_COLUMNS } from '@/modules/entity-actions-review/constants';

    // PRODUCT-26110 Task 13 follow-up: declares every event this component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'scroll-end' but
    // it is neither declared in the emits option nor as an 'onScrollEnd' prop" every
    // time it fires (seen x22 in live QA; `scroll-end` is forwarded here from
    // InfiniteScroll's own `scroll` event).
    @Component({
        components: {
            EidIcon, HeadCell, EntityActionsTableRow, LineLoader, Table, InfiniteScroll,
        },
        emits: [ 'review', 'scroll-end' ],
    })
    export default class EntityActionsTable extends Vue {
        @Prop({ type: Array, default: () => [] })
        private entityActions!: ActionReview[];

        @Prop({ type: String })
        private noActionMessage?: string;

        @Prop({ type: Boolean, default: false })
        private loading!: boolean;

        @Prop({ type: Boolean, default: false })
        private finished!: boolean;

        @Prop({ type: Object, default: {} })
        private requirementDefinitions!: Record<string, TprmRequirementDefinition>;

        @Prop({ type: String as PropType<ActionsTab>, default: ActionsTab.pending })
        private tab!: ActionsTab;

        @Prop({ type: Number, default: null })
        private total!: number | null;

        @Prop({ type: String, default: '' })
        private searchTerm!: string;

        @Prop({ type: Object, default: null })
        private sorting!: Sorting | null;

        // Mirrors the "Sort by" control above the table rather than replacing it: the header shows
        // which way the column is ordered, the control remains the way to change it.
        private get dateCreatedSortIcon(): IconDefinition | null {
            if (!this.sorting || this.sorting.column !== ACTIONS_SORT_COLUMNS.createdAt) {
                return null;
            }
            return this.sorting.direction === SortingDirection.asc ? faArrowUp : faArrowDown;
        }

        private get isCompletedTab(): boolean {
            return this.tab === ActionsTab.completed;
        }

        private get emptyReviewsText(): string {
            return this.noActionMessage || 'All available actions have been completed.';
        }

        // The backend returns -1 (not null) for an unrequested total, so a negative value
        // is treated the same as a missing one rather than shown to the admin verbatim.
        private get displayedTotal(): number {
            return this.total == null || this.total < 0 ? this.entityActions.length : this.total;
        }
    }
</script>
